/**
 * Charter (5th TCS platform) — shared types for the Osprey back-office.
 *
 * These mirror charter-api's DTOs (src/domain/ *.controller.ts + dto/*.dto.ts).
 * charter-api runs under the `/api/v1` global prefix; Osprey reaches it through
 * osprey-api (single-origin) at the `/charter/*` proxy path — see api/charterApi.ts.
 *
 * All monetary amounts are in minor units (₦ kobo). Charter providers operate in
 * WAT (Africa/Lagos); timestamps on the wire are ISO-8601 UTC and rendered in WAT.
 */

// ── Shared enums (charter-api shared/dto/common.dto.ts) ──────────────────────

export const DEPOSIT_RULE_TYPES = ['none', 'percentage', 'full'] as const;
export type DepositRuleType = (typeof DEPOSIT_RULE_TYPES)[number];

export interface DepositRule {
  type: DepositRuleType;
  /** Required when type = 'percentage' — 1..100. */
  percentageValue?: number;
}

export const GATEWAY_STATES = ['open', 'waitlist', 'closed'] as const;
export type GatewayState = (typeof GATEWAY_STATES)[number];

export const DEPOSIT_RULE_LABELS: Record<DepositRuleType, string> = {
  none: 'No deposit (pay in full at booking)',
  percentage: 'Percentage deposit',
  full: 'Full payment upfront',
};

export const GATEWAY_STATE_LABELS: Record<GatewayState, string> = {
  open: 'Open',
  waitlist: 'Waitlist',
  closed: 'Closed',
};

/** Add-on provenance — merchant-created vs created by a TCS admin on the provider's behalf. */
export const ADDON_SOURCES = ['MERCHANT', 'TCS_ADMIN'] as const;
export type AddonSource = (typeof ADDON_SOURCES)[number];

// Charter has no fixed WAT-only currency lock; NGN is the platform default.
export const CHARTER_CURRENCIES = ['NGN', 'USD'] as const;
export type CharterCurrency = (typeof CHARTER_CURRENCIES)[number];

/** List envelope from charter-api: `{ data, meta }`. */
export interface Paginated<T> {
  data: T[];
  meta: { total: number; page: number; limit: number };
}

export interface PaginationQuery {
  page?: number;
  limit?: number;
  search?: string;
}

// ── Services (charter-api domain/services) ───────────────────────────────────

export interface CharterService {
  id: string;
  name: string;
  description?: string | null;
  durationMinutes: number;
  /** Minor units. 0 = free (payment step skipped). */
  price: number;
  currency?: string;
  depositRule: DepositRule;
  archived?: boolean;
}

export interface CreateServicePayload {
  name: string;
  description?: string;
  durationMinutes: number;
  price: number;
  depositRule: DepositRule;
}

export type UpdateServicePayload = Partial<CreateServicePayload>;

// ── Packages (charter-api domain/packages) ───────────────────────────────────

export interface CharterPackage {
  id: string;
  name: string;
  description?: string | null;
  serviceIds: string[];
  price: number;
  currency?: string;
  depositRule: DepositRule;
  /** Whether buying it reserves a calendar slot, or is delivered async. */
  requiresSlot: boolean;
  archived?: boolean;
}

export interface CreatePackagePayload {
  name: string;
  description?: string;
  serviceIds: string[];
  price: number;
  depositRule: DepositRule;
  requiresSlot: boolean;
}

export type UpdatePackagePayload = Partial<CreatePackagePayload>;

// ── Add-ons (charter-api domain/addons) ──────────────────────────────────────

export interface CharterAddon {
  id: string;
  name: string;
  price: number;
  currency?: string;
  extraDurationMinutes?: number;
  packageIds?: string[];
  /** MERCHANT (provider-created) or TCS_ADMIN (created on the provider's behalf). */
  source: AddonSource;
  archived?: boolean;
}

export interface CreateAddonPayload {
  name: string;
  price: number;
  extraDurationMinutes?: number;
  packageIds?: string[];
}

export type UpdateAddonPayload = Partial<CreateAddonPayload>;

// ── Bookings & Orders (shared shapes) ────────────────────────────────────────

export interface CharterCustomer {
  name: string;
  email: string;
  phone?: string;
  timezone?: string;
}

export interface CharterSlot {
  id: string;
  startsAt: string;
  endsAt: string;
  available?: boolean;
}

export interface Money {
  amount: number;
  currency: string;
}

/** Portfolio work referenced as inspiration on a booking/order detail. */
export interface CharterInspiration {
  id: string;
  title: string;
  caption?: string;
  media?: CharterMedia[];
}

export type CharterBookingStatus =
  | 'PENDING_PAYMENT'
  | 'CONFIRMED'
  | 'SCHEDULED'
  | 'RESCHEDULED'
  | 'CANCELLED'
  | 'COMPLETED';

export type CharterPaymentStatus =
  | 'UNPAID'
  | 'DEPOSIT_PAID'
  | 'PAID'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED';

export interface CharterBooking {
  id: string;
  serviceId?: string;
  serviceIds?: string[];
  status: CharterBookingStatus;
  slot?: CharterSlot | null;
  paymentStatus?: CharterPaymentStatus;
  deposit?: Money | null;
  remainingBalance?: number;
  durationMinutes?: number;
  currency?: string;
  customer?: CharterCustomer;
  portfolioWorkId?: string | null;
  inspiration?: CharterInspiration | null;
  formData?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

/** A bundled session on a (slot-less) package order that the provider schedules manually. */
export interface CharterOrderSession {
  stepId: string;
  serviceId: string;
  status: 'UNSCHEDULED' | 'SCHEDULED' | 'COMPLETED';
  slotId?: string;
  startsAt?: string;
}

export interface CharterAddonSelection {
  addonId: string;
  quantity?: number;
}

export interface CharterOrder {
  id: string;
  packageId?: string;
  status: CharterBookingStatus;
  requiresSlot?: boolean;
  slot?: CharterSlot | null;
  addons?: CharterAddonSelection[];
  paymentStatus?: CharterPaymentStatus;
  deposit?: Money | null;
  remainingBalance?: number;
  currency?: string;
  customer?: CharterCustomer;
  portfolioWorkId?: string | null;
  inspiration?: CharterInspiration | null;
  sessions?: CharterOrderSession[];
  formData?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface BalancePaymentLink {
  paymentUrl: string;
  amount: number;
  currency: string;
}

/** Response of a duration override: the updated booking + any resulting slot/capacity conflicts. */
export interface OverrideDurationResult extends CharterBooking {
  conflicts?: CharterConflict[];
}

export interface CharterConflict {
  bookingId?: string;
  slotId?: string;
  startsAt?: string;
  endsAt?: string;
  reason?: string;
}

// Action payloads
export interface CancelPayload {
  reason?: string;
}

export interface ReschedulePayload {
  slotId: string;
}

export interface OverrideDurationPayload {
  durationMinutes: number;
}

/** Merchant refund decision on an order cancel (partial/full refund or store credit). */
export interface RefundDecisionPayload {
  refundAmount?: number;
  credit?: boolean;
  reason?: string;
}

export interface ScheduleSessionPayload {
  sessionStepId: string;
  slotId?: string;
  startsAt?: string;
}

// ── Provider profile (charter-api domain/merchants) ──────────────────────────

export interface CharterCancellationPolicy {
  policyText?: string;
  cancellationNoticeHours?: number;
  rescheduleNoticeHours?: number;
}

export interface CharterBranding {
  displayName?: string;
  tagline?: string;
  bio?: string;
  logoUrl?: string;
  heroUrl?: string;
  gallery?: string[];
}

export interface CharterProvider {
  merchantId: string;
  solutionId?: string;
  branding?: CharterBranding;
  gatewayState: GatewayState;
  timezone?: string;
  cancellationPolicy?: CharterCancellationPolicy;
}

export interface UpdateProviderProfilePayload {
  displayName?: string;
  tagline?: string;
  bio?: string;
  timezone?: string;
}

export interface UpdateBrandAssetsPayload {
  logoUrl?: string;
  heroUrl?: string;
  gallery?: string[];
}

export interface UpdateGatewayStatePayload {
  state: GatewayState;
}

// ── Availability (charter-api domain/availability) ───────────────────────────

export interface AvailabilityWindow {
  /** 0 = Sunday .. 6 = Saturday. */
  dayOfWeek: number;
  opensAt: string;
  closesAt: string;
}

export interface AvailabilityOverride {
  id?: string;
  date: string;
  closed?: boolean;
  opensAt?: string;
  closesAt?: string;
}

export interface Availability {
  timezone: string;
  windows: AvailabilityWindow[];
  overrides?: AvailabilityOverride[];
}

export interface SetAvailabilityPayload {
  timezone: string;
  windows: AvailabilityWindow[];
}

// ── Intake form (charter-api domain/intake) ──────────────────────────────────

export interface IntakeField {
  key: string;
  type: string;
  label: string;
  required?: boolean;
  helpText?: string;
  order?: number;
  validation?: { options?: string[] };
}

export interface IntakeForm {
  solutionId?: string;
  domain?: string;
  fields: IntakeField[];
}

export interface SetIntakeFormPayload {
  fields: IntakeField[];
}

// ── Portfolio (charter-api domain/portfolio) ─────────────────────────────────

export interface CharterMedia {
  id: string;
  cdnUrl: string;
  type?: string;
}

export interface PortfolioWork {
  id: string;
  title: string;
  caption?: string | null;
  media: CharterMedia[];
  serviceIds?: string[];
}

export interface CreatePortfolioWorkPayload {
  title: string;
  caption?: string;
  mediaIds: string[];
  serviceIds?: string[];
}

export type UpdatePortfolioWorkPayload = Partial<CreatePortfolioWorkPayload>;

// ── Waitlist (charter-api domain/waitlist) ───────────────────────────────────

export type WaitlistStatus = 'WAITING' | 'CONTACTED' | 'CONVERTED' | 'CLOSED';

export const WAITLIST_STATUSES: WaitlistStatus[] = ['WAITING', 'CONTACTED', 'CONVERTED', 'CLOSED'];

export interface WaitlistEntry {
  id: string;
  status: WaitlistStatus;
  serviceId?: string;
  customer: CharterCustomer;
  note?: string;
  createdAt?: string;
}

export interface UpdateWaitlistEntryPayload {
  status?: WaitlistStatus;
  note?: string;
}

// ── Display labels ───────────────────────────────────────────────────────────

export const BOOKING_STATUS_LABELS: Record<string, string> = {
  PENDING_PAYMENT: 'Pending payment',
  CONFIRMED: 'Confirmed',
  SCHEDULED: 'Scheduled',
  RESCHEDULED: 'Rescheduled',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed',
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  UNPAID: 'Unpaid',
  DEPOSIT_PAID: 'Deposit paid',
  PAID: 'Paid',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Partially refunded',
};

export const WAITLIST_STATUS_LABELS: Record<WaitlistStatus, string> = {
  WAITING: 'Waiting',
  CONTACTED: 'Contacted',
  CONVERTED: 'Converted',
  CLOSED: 'Closed',
};
